import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "../ui/Modal";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { Spinner } from "../ui/Spinner";
import { useToast } from "../ui/Toast";
import { repoHome } from "../lib/paths";
import { useTranslation } from "../i18n";
import {
  useGitConnections,
  useConnectionRepos,
  useConnectRepo,
  type RemoteRepoOption,
} from "./useConnect.js";

const KEBAB_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly wsSlug: string;
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function ConnectRepoModal({ open, onClose, wsSlug }: Props) {
  const { t } = useTranslation("repos");
  const navigate = useNavigate();
  const toast = useToast();

  const connections = useGitConnections(wsSlug);
  const conns = useMemo(() => connections.data?.connections ?? [], [connections.data]);

  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [remoteKey, setRemoteKey] = useState<string>("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [pathPrefix, setPathPrefix] = useState("");
  const [filter, setFilter] = useState<"blob:none" | "tree:0">("blob:none");

  const connect = useConnectRepo(wsSlug);

  // Reset on open, THEN default-select the connection last — in ONE effect so
  // the reset can't clobber the default selection. `conns` is often already
  // cached (useGitConnections isn't gated on `open`), so a second effect keyed
  // on `open` would race this one and null connectionId back out.
  useEffect(() => {
    if (!open) return;
    setRemoteKey("");
    setSlug("");
    setSlugTouched(false);
    setPathPrefix("");
    setFilter("blob:none");
    connect.reset();
    setConnectionId(conns[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Late-arriving connections: if they loaded AFTER the modal opened (fresh
  // page load, cache cold), default-select once available. Keyed on `conns`
  // (not `open`), and guarded on `!connectionId`, so it never overrides the
  // reset effect's selection or a user's choice.
  useEffect(() => {
    if (open && !connectionId && conns.length > 0) setConnectionId(conns[0]!.id);
  }, [open, connectionId, conns]);

  const repos = useConnectionRepos(wsSlug, connectionId);
  const remotes = useMemo(() => repos.data?.repos ?? [], [repos.data]);
  const selected: RemoteRepoOption | undefined = useMemo(
    () => remotes.find((r) => `${r.owner}/${r.name}` === remoteKey),
    [remotes, remoteKey],
  );

  // When a repo is picked, seed the slug + suggested filter (unless user edited).
  useEffect(() => {
    if (!selected) return;
    if (!slugTouched) setSlug(toSlug(selected.name));
    setFilter(selected.suggestedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteKey]);

  const prefixTrimmed = pathPrefix.trim();
  const tree0NeedsPrefix = filter === "tree:0" && prefixTrimmed.length === 0;
  const slugValid = KEBAB_RE.test(slug.trim());
  const canSubmit =
    !!selected && slugValid && !tree0NeedsPrefix && connect.phase !== "cloning";

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || !selected || !connectionId) return;
    void connect.start({
      connectionId,
      owner: selected.owner,
      repo: selected.name,
      slug: slug.trim(),
      defaultBranch: selected.defaultBranch,
      pathPrefix: prefixTrimmed || undefined,
      cloneFilter: filter,
    });
  }

  const loading = connections.isLoading;
  const noConnections = !loading && conns.length === 0;

  return (
    <Modal open={open} onClose={onClose} title={t("connect.title")}>
      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : noConnections ? (
        <InstallStep wsSlug={wsSlug} />
      ) : connect.phase === "cloning" || connect.phase === "done" || connect.phase === "error" ? (
        <ProgressStep
          connect={connect}
          onOpenRepo={() => {
            if (connect.repo) {
              onClose();
              toast.success(t("connect.toastConnected", { slug: connect.repo.slug }));
              navigate(repoHome(wsSlug, connect.repo.slug));
            }
          }}
          onClose={onClose}
          onRetry={() => connect.reset()}
        />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {conns.length > 1 && (
            <Field label={t("connect.connectionLabel")}>
              {({ id }) => (
                <select
                  id={id}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
                  value={connectionId ?? ""}
                  onChange={(e) => {
                    setConnectionId(e.target.value);
                    setRemoteKey("");
                  }}
                >
                  {conns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.accountLogin}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          <Field label={t("connect.repoLabel")}>
            {({ id }) => (
              <select
                id={id}
                className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
                value={remoteKey}
                onChange={(e) => setRemoteKey(e.target.value)}
                disabled={repos.isLoading}
              >
                <option value="">
                  {repos.isLoading ? t("connect.reposLoading") : t("connect.repoPlaceholder")}
                </option>
                {remotes.map((r) => (
                  <option key={`${r.owner}/${r.name}`} value={`${r.owner}/${r.name}`}>
                    {r.owner}/{r.name}
                    {r.private ? ` · ${t("connect.private")}` : ""}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {selected && (
            <>
              <Field
                label={t("connect.slugLabel")}
                error={slugTouched && slug && !slugValid ? t("create.slugPattern") : undefined}
              >
                {({ id, ...aria }) => (
                  <TextInput
                    id={id}
                    value={slug}
                    autoComplete="off"
                    onChange={(e) => {
                      setSlugTouched(true);
                      setSlug(e.target.value);
                    }}
                    hasError={slugTouched && !!slug && !slugValid}
                    {...aria}
                  />
                )}
              </Field>

              <Field label={t("connect.pathPrefixLabel")}>
                {({ id, ...aria }) => (
                  <>
                    <TextInput
                      id={id}
                      value={pathPrefix}
                      autoComplete="off"
                      placeholder={t("connect.pathPrefixPlaceholder")}
                      onChange={(e) => setPathPrefix(e.target.value)}
                      {...aria}
                    />
                    <p className="text-xs text-slate-500">{t("connect.pathPrefixHint")}</p>
                  </>
                )}
              </Field>

              <Field
                label={t("connect.filterLabel")}
                error={tree0NeedsPrefix ? t("connect.tree0NeedsPrefix") : undefined}
              >
                {({ id }) => (
                  <select
                    id={id}
                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value as "blob:none" | "tree:0")}
                  >
                    <option value="blob:none">{t("connect.filterBlobNone")}</option>
                    <option value="tree:0">{t("connect.filterTree0")}</option>
                  </select>
                )}
              </Field>

              <p className="text-xs text-slate-500">
                {t("connect.sizeHint", { kb: selected.sizeKb.toLocaleString() })}
              </p>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t("connect.cancel")}
            </Button>
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {t("connect.submit")}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function InstallStep({ wsSlug }: { wsSlug: string }) {
  const { t } = useTranslation("repos");
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">{t("connect.installIntro")}</p>
      <Button
        variant="primary"
        onClick={() => {
          // Full navigation to GitHub; the setup callback redirects back with
          // ?connect=github. Relative URL keeps it proxy-tolerant.
          window.location.href = `/api/ws/${encodeURIComponent(wsSlug)}/github/install`;
        }}
      >
        {t("connect.installButton")}
      </Button>
    </div>
  );
}

function ProgressStep({
  connect,
  onOpenRepo,
  onClose,
  onRetry,
}: {
  connect: ReturnType<typeof useConnectRepo>;
  onOpenRepo: () => void;
  onClose: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation("repos");

  if (connect.phase === "error") {
    return (
      <div className="space-y-4">
        <Banner kind="error">{connect.error ?? t("connect.cloneFailed")}</Banner>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("connect.cancel")}
          </Button>
          <Button type="button" variant="primary" onClick={onRetry}>
            {t("connect.tryAgain")}
          </Button>
        </div>
      </div>
    );
  }

  const done = connect.phase === "done";
  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1 flex justify-between text-xs text-slate-400">
          <span>{done ? t("connect.cloneComplete") : t("connect.cloning")}</span>
          <span>{connect.percent}%</span>
        </div>
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-slate-800"
          role="progressbar"
          aria-valuenow={connect.percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full bg-arch-500 transition-[width] duration-150"
            style={{ width: `${connect.percent}%` }}
          />
        </div>
        {connect.lastLine && !done && (
          <p className="mt-2 truncate font-mono text-xs text-slate-500">{connect.lastLine}</p>
        )}
      </div>

      {done && connect.summary && (
        <dl className="grid grid-cols-2 gap-3 rounded-lg border border-slate-800 bg-slate-900/50 p-4 text-sm">
          <div>
            <dt className="text-xs text-slate-500">{t("connect.summaryFiles")}</dt>
            <dd className="text-lg font-medium text-slate-200">{connect.summary.supportedFileCount}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">{t("connect.summaryHistory")}</dt>
            <dd className="text-lg font-medium text-slate-200">{connect.summary.historyDepth}</dd>
          </div>
        </dl>
      )}

      <div className="flex justify-end gap-2">
        {done ? (
          <Button type="button" variant="primary" onClick={onOpenRepo}>
            {t("connect.openRepo")}
          </Button>
        ) : (
          <Button type="button" variant="ghost" disabled>
            {t("connect.cloning")}
          </Button>
        )}
      </div>
    </div>
  );
}
